import { getProductCalculationInputs } from './calculate/get-product-calculation-inputs';
import {
	calculateProductPricing,
	CalculateProductPricingResult,
} from './calculate/calculate-product-pricing';
import {
	createContext,
	useContext,
	PropsWithChildren,
	useState,
	useEffect,
	useCallback,
} from 'react';

import { Config, InputValues } from './types.ts';

const PricingCalculatorContext = createContext<PricingCalculatorContextValue | undefined>(
	undefined,
);

export interface PricingCalculatorContextValue {
	inputValues: InputValues;
	changeInputValues: (inputValues: Partial<InputValues>) => CalculateProductPricingResult;
	calculationResult: CalculateProductPricingResult;
	config: Config;
	getDiscountedPrice?: (args: GetDiscountedPriceArgs) => number;
}

export const usePricingCalculatorContext = () => {
	const pricingCalculatorContext = useContext(PricingCalculatorContext);
	if (!pricingCalculatorContext) {
		throw new Error('usePricingCalculatorContext must be used within a PricingCalculatorProvider');
	}
	return pricingCalculatorContext;
};

export interface PricingCalculatorContextProviderProps extends PropsWithChildren {
	config: Config;
	inputValues: InputValues;
	onChangeInputs?: (value: InputValues) => void;
	getDiscountedPrice?: (args: GetDiscountedPriceArgs) => number;
}

export interface GetDiscountedPriceArgs {
	price: number;
	inputs: InputValues;
}

export const PricingCalculatorContextProvider = (props: PricingCalculatorContextProviderProps) => {
	const [inputValues, setInputValues] = useState<InputValues>(props.inputValues);

	const calculate = useCallback(
		(v: InputValues) => {
			const { config } = props;
			const productCalculationInputs = getProductCalculationInputs(
				config.planLimitConfigs,
				config.unitMinEnterprise,
				v.unitCount,
				v.billingCycle,
			);

			return calculateProductPricing(
				config.ccpPricingData,
				productCalculationInputs,
				config.currencyISOCode,
				config.localeISOCode,
			);
		},
		[props],
	);

	const [calculationResult, setCalculationResult] = useState<CalculateProductPricingResult>(() =>
		calculate(inputValues),
	);

	// Recalculate when config (including currency) changes
	useEffect(() => {
		const result = calculate(inputValues);
		setCalculationResult(result);
	}, [props.config, inputValues, calculate]);

	const changeInputValues = (changes: Partial<InputValues>): CalculateProductPricingResult => {
		const nextValues = {
			...inputValues,
			...changes,
		};
		setInputValues(nextValues);
		props.onChangeInputs?.(nextValues);
		const result = calculate(nextValues);
		setCalculationResult(result);
		return result;
	};

	return (
		<PricingCalculatorContext.Provider
			value={{
				inputValues,
				changeInputValues,
				calculationResult,
				config: props.config,
				getDiscountedPrice: props.getDiscountedPrice,
			}}
		>
			{props.children}
		</PricingCalculatorContext.Provider>
	);
};
